import { useState } from "react";
import type { FormEvent } from "react";
import { Link } from "react-router-dom";
import PageMeta from "@/components/base/PageMeta";
import SiteLayout from "@/components/feature/SiteLayout";

const services = [
  {
    icon: "ri-layout-grid-line",
    title: "清晰的分类浏览",
    desc: "8 大题材分类搭配子题材标签、地区与连载状态筛选，帮你快速缩小范围。",
  },
  {
    icon: "ri-flashlight-line",
    title: "连载更新追踪",
    desc: "更新页按时间汇总最新章节，打开就知道哪些作品有新内容。",
  },
  {
    icon: "ri-search-line",
    title: "全站关键词搜索",
    desc: "支持漫画名、作者、简介与标签匹配，搜索结果也能继续筛选。",
  },
  {
    icon: "ri-sparkling-line",
    title: "相关推荐",
    desc: "依据题材与标签给出相似作品，读完一部还能顺着味道继续找。",
  },
];

const notices = [
  {
    title: "内容说明",
    text: "本页面展示的漫画名称、作者、简介与封面均为演示数据，用于呈现平台的信息结构与浏览体验。",
  },
  {
    title: "版权声明",
    text: "站内出现的作品与插图版权归原作者及出版方所有。若你是权利人并希望调整展示内容，可通过下方反馈表单联系我们。",
  },
  {
    title: "功能范围",
    text: "当前版本提供浏览、分类、筛选、搜索与作品详情展示。章节阅读页、收藏与账号体系仍在规划中。",
  },
];

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError("");
    const form = event.currentTarget;
    const formData = new FormData(form);
    const honeypot = String(formData.get("contact_alt") ?? "").trim();

    if (honeypot) {
      setFormStatus("success");
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "contact_alt") {
        return;
      }
      if (typeof value === "string" && value.trim()) {
        payload.append(key, value);
      }
    });

    setFormStatus("submitting");
    try {
      const response = await fetch("https://readdy.ai/api/form/db9lfmhrh5tg6sock7ag", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMessage =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = `${serverMessage}`.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setFormStatus("success");
        setFormError("");
        form.reset();
      } else {
        setFormStatus("error");
        setFormError(serverMessage || "提交失败，请稍后再试。");
      }
    } catch {
      setFormStatus("error");
      setFormError("网络异常，反馈没有发送成功，请稍后再试。");
    }
  };

  return (
    <SiteLayout>
      <PageMeta
        title="关于我们 - JMComic漫画频道 | 精选漫画阅读平台"
        description="了解JMComic漫画频道的定位与内容范围，查看用户须知、版权声明，并通过反馈表单与我们联系。"
        keywords="JMComic,关于我们,漫画平台介绍,版权声明,漫画反馈"
        path="/about"
      />

      <div className="border-b border-background-200 bg-background-50">
        <div className="mx-auto w-full max-w-[1240px] px-4 py-8 md:px-6 md:py-12">
          <nav aria-label="面包屑" className="flex items-center gap-1.5 text-xs text-foreground-500">
            <Link to="/" className="hover:text-primary-700">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-800">关于我们</span>
          </nav>
          <div className="mt-4 grid grid-cols-1 gap-6 lg:grid-cols-12 lg:items-center">
            <div className="lg:col-span-7">
              <span className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-primary-600">
                <span className="h-1.5 w-1.5 rounded-full bg-accent-500"></span>
                About JMComic
              </span>
              <h1 className="mt-2 font-heading text-3xl font-extrabold leading-tight tracking-tight text-foreground-950 md:text-4xl">
                关于 JMComic 漫画频道
              </h1>
              <p className="mt-4 max-w-2xl text-sm leading-[1.9] text-foreground-600 md:text-base">
                JMComic 是一个面向漫画爱好者的精选漫画频道。我们相信找漫画这件事不该靠运气：
                把题材分类、连载状态、更新时间与作品资料整理清楚，读者就能花更少的力气，读到更合适的作品。
              </p>
              <p className="mt-3 max-w-2xl text-sm leading-[1.9] text-foreground-500">
                频道目前聚合热血、恋爱、奇幻、都市、悬疑、校园、科幻、古风八大题材，
                并通过分类页与搜索页提供多维度筛选，让连载追踪者与分类浏览者都能找到自己的路径。
              </p>
            </div>
            <div className="lg:col-span-5">
              <div className="grid grid-cols-3 gap-3">
                {[
                  { value: "20+", label: "精选作品", icon: "ri-book-marked-line" },
                  { value: "8", label: "题材分类", icon: "ri-layout-grid-line" },
                  { value: "每日", label: "连载速览", icon: "ri-calendar-check-line" },
                ].map((stat) => (
                  <div
                    key={stat.label}
                    className="rounded-lg border border-background-200 bg-background-50 p-3 text-center md:p-4"
                  >
                    <span className="mx-auto flex h-9 w-9 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                      <i className={`${stat.icon} text-lg`}></i>
                    </span>
                    <span className="mt-2 block font-heading text-lg font-extrabold text-foreground-950">
                      {stat.value}
                    </span>
                    <span className="block text-[10px] text-foreground-500">{stat.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1240px] px-4 py-10 md:px-6 md:py-12">
        <section>
          <div className="mb-5">
            <span className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-primary-600">
              <span className="h-1.5 w-1.5 rounded-full bg-accent-500"></span>
              What We Offer
            </span>
            <h2 className="font-heading text-2xl font-bold tracking-tight text-foreground-950">
              我们提供的阅读服务
            </h2>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:gap-4 lg:grid-cols-4">
            {services.map((service, index) => (
              <article
                key={service.title}
                className="rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300"
              >
                <span
                  className={`flex h-10 w-10 items-center justify-center rounded-md ${
                    index % 3 === 0
                      ? "bg-primary-100 text-primary-700"
                      : index % 3 === 1
                        ? "bg-accent-100 text-accent-700"
                        : "bg-secondary-100 text-secondary-700"
                  }`}
                >
                  <i className={`${service.icon} text-xl`}></i>
                </span>
                <h3 className="mt-3 font-heading text-sm font-bold text-foreground-950">
                  {service.title}
                </h3>
                <p className="mt-1.5 text-xs leading-relaxed text-foreground-500">{service.desc}</p>
              </article>
            ))}
          </div>
        </section>

        <div className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <section id="feedback" className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
                <i className="ri-chat-3-line text-lg text-primary-500"></i>
                联系我们 / 提交反馈
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-foreground-500">
                对分类、搜索或内容有任何建议，都可以写在这里。我们会认真阅读每一条反馈。
              </p>

              <form
                id="jmcomic-feedback-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="mt-5 space-y-4"
              >
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label
                      htmlFor="feedback-name"
                      className="mb-1.5 block text-xs font-medium text-foreground-700"
                    >
                      昵称
                    </label>
                    <input
                      id="feedback-name"
                      name="name"
                      type="text"
                      required
                      maxLength={40}
                      placeholder="怎么称呼你"
                      className="h-11 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="feedback-email"
                      className="mb-1.5 block text-xs font-medium text-foreground-700"
                    >
                      邮箱
                    </label>
                    <input
                      id="feedback-email"
                      name="email"
                      type="email"
                      required
                      placeholder="you@example.com"
                      className="h-11 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="feedback-type"
                    className="mb-1.5 block text-xs font-medium text-foreground-700"
                  >
                    反馈类型
                  </label>
                  <select
                    id="feedback-type"
                    name="type"
                    required
                    defaultValue=""
                    className="h-11 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-800 outline-none transition-colors focus:border-primary-400"
                  >
                    <option value="" disabled>
                      请选择反馈类型
                    </option>
                    <option value="内容建议">内容建议</option>
                    <option value="分类与筛选">分类与筛选</option>
                    <option value="功能需求">功能需求</option>
                    <option value="版权相关">版权相关</option>
                    <option value="其他问题">其他问题</option>
                  </select>
                </div>

                <div>
                  <label
                    htmlFor="feedback-message"
                    className="mb-1.5 flex items-center justify-between text-xs font-medium text-foreground-700"
                  >
                    反馈内容
                    <span className="text-[10px] font-normal text-foreground-400">最多 500 字</span>
                  </label>
                  <textarea
                    id="feedback-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    placeholder="说说你的想法，例如希望增加哪些题材或功能"
                    className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm leading-relaxed outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                  ></textarea>
                </div>

                <input
                  type="text"
                  name="contact_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="feedback-extra-field"
                />

                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-[11px] leading-relaxed text-foreground-400">
                    提交即表示你同意我们仅将信息用于本次反馈沟通。
                  </p>
                  <button
                    type="submit"
                    disabled={formStatus === "submitting"}
                    className={`flex h-11 items-center justify-center gap-1.5 whitespace-nowrap rounded-md px-5 text-sm font-semibold transition-colors ${
                      formStatus === "submitting"
                        ? "cursor-not-allowed bg-primary-300 text-background-50"
                        : "bg-primary-500 text-background-50 hover:bg-primary-600"
                    }`}
                  >
                    {formStatus === "submitting" ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        提交中
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-line"></i>
                        提交反馈
                      </>
                    )}
                  </button>
                </div>

                {formStatus === "success" && (
                  <div className="flex items-start gap-2 rounded-md border border-primary-200 bg-primary-50 px-3 py-2.5 text-xs text-primary-900">
                    <i className="ri-checkbox-circle-line mt-0.5 text-base text-primary-600"></i>
                    <p>反馈已提交，谢谢你的建议！我们会尽快查看。</p>
                  </div>
                )}
                {formStatus === "error" && (
                  <div className="flex items-start gap-2 rounded-md border border-accent-200 bg-accent-50 px-3 py-2.5 text-xs text-accent-900">
                    <i className="ri-error-warning-line mt-0.5 text-base text-accent-600"></i>
                    <p>{formError || "提交失败，请稍后再试。"}</p>
                  </div>
                )}
              </form>
            </section>

            <section className="mt-6 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <h2 className="font-heading text-lg font-bold text-foreground-950">用户须知与版权声明</h2>
              <div className="mt-4 space-y-4">
                {notices.map((notice) => (
                  <div key={notice.title} className="rounded-md border border-background-200 bg-background-50 p-3.5">
                    <h3 className="text-sm font-semibold text-foreground-900">{notice.title}</h3>
                    <p className="mt-1 text-xs leading-relaxed text-foreground-500">{notice.text}</p>
                  </div>
                ))}
              </div>
            </section>
          </div>

          <aside className="space-y-4 lg:col-span-5">
            <section className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">我们的原则</h2>
              <ul className="mt-3 space-y-3">
                {[
                  "分类与标签保持准确，减少无效点击。",
                  "详情页信息完整，作者、状态与更新一眼可查。",
                  "不夸大功能，未上线能力会明确说明。",
                  "尊重原作者与权利人的版权诉求。",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2 text-xs leading-relaxed text-foreground-600">
                    <i className="ri-checkbox-blank-circle-fill mt-1 text-[6px] text-primary-500"></i>
                    {item}
                  </li>
                ))}
              </ul>
            </section>

            <section className="rounded-lg border border-primary-200 bg-primary-50 p-5">
              <h2 className="font-heading text-base font-bold text-primary-900">常见问题</h2>
              <div className="mt-3 space-y-3">
                <div>
                  <h3 className="text-xs font-semibold text-primary-900">章节可以点开阅读吗？</h3>
                  <p className="mt-1 text-[11px] leading-relaxed text-primary-800/80">
                    章节列表目前为信息展示，阅读页正在开发中，点击章节会提示功能状态。
                  </p>
                </div>
                <div>
                  <h3 className="text-xs font-semibold text-primary-900">需要注册账号吗？</h3>
                  <p className="mt-1 text-[11px] leading-relaxed text-primary-800/80">
                    当前版本不需要注册，也没有会员体系，所有浏览与筛选功能都可直接使用。
                  </p>
                </div>
                <div>
                  <h3 className="text-xs font-semibold text-primary-900">如何反馈问题？</h3>
                  <p className="mt-1 text-[11px] leading-relaxed text-primary-800/80">
                    使用左侧反馈表单即可，我们会通过你留下的邮箱回复。
                  </p>
                </div>
              </div>
            </section>

            <section className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">快速入口</h2>
              <div className="mt-3 grid grid-cols-2 gap-2">
                {[
                  { label: "全部漫画", to: "/category/all", icon: "ri-layout-grid-line" },
                  { label: "最新更新", to: "/updates", icon: "ri-flashlight-line" },
                  { label: "搜索漫画", to: "/search", icon: "ri-search-line" },
                  { label: "返回首页", to: "/", icon: "ri-home-5-line" },
                ].map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className="flex items-center gap-2 rounded-md border border-background-200 px-2.5 py-2 text-xs font-medium text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-700"
                  >
                    <i className={`${link.icon} text-sm text-primary-600`}></i>
                    {link.label}
                  </Link>
                ))}
              </div>
            </section>
          </aside>
        </div>
      </div>
    </SiteLayout>
  );
}